import React from "react";
import { Link } from "react-router-dom";
import { Award, FileCheck2, ZoomIn } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Reveal, Kicker } from "../motion";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "../ui/dialog";
import kazagroDiploma2011 from "../../assets/awards/kazagro-diploma-2011.webp";
import minselkhozGramota2013 from "../../assets/awards/minselkhoz-gramota-2013.webp";
import fundAgroSupportGramota from "../../assets/awards/fund-agro-support-gramota.webp";
import ebrdCamco2013 from "../../assets/awards/ebrd-camco-2013.webp";
import ebrdCamco2015 from "../../assets/awards/ebrd-camco-2015.webp";
import kyrgyzstanInternship2016 from "../../assets/awards/kyrgyzstan-internship-2016.webp";
import transparencyImage from "../../assets/advantages-transparency.webp";
import speedImage from "../../assets/advantages-speed.webp";
import partnershipImage from "../../assets/advantages-partnership.webp";

const VALUE_IMAGES = [transparencyImage, speedImage, partnershipImage];

// Order must mirror `about.awards` in i18n/content.js for both locales.
const AWARD_IMAGES = [
  kazagroDiploma2011,
  minselkhozGramota2013,
  fundAgroSupportGramota,
  ebrdCamco2013,
  ebrdCamco2015,
  kyrgyzstanInternship2016,
];

export function About({ hideIntro = false }) {
  const { c, lang } = useApp();
  const a = c.about;
  const licenseImage = lang === "kz"
    ? "/documents/license-07-21-0001-m-kz.jpg"
    : "/documents/license-07-21-0001-m-ru.jpg";

  return (
    <section
      id="about"
      data-testid="about-section"
      className={`topo relative bg-[#f5f7fa] ${hideIntro ? "py-6 md:py-10" : "py-24 md:py-32"}`}
    >
      <div className="mx-auto max-w-[1240px] px-5 md:px-8">
        {!hideIntro && <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
          <Reveal>
            <Kicker>{a.kicker}</Kicker>
            <h2 className="mt-5 font-display text-4xl font-bold tracking-tight text-[#0a1a2e] sm:text-5xl">
              {a.title}
            </h2>
            <p className="mt-6 font-display text-xl font-medium leading-snug text-[#002355]">
              {a.lead}
            </p>
          </Reveal>

          <Reveal delay={0.1} className="space-y-5 lg:pt-16">
            {a.paragraphs.map((para, i) => (
              <p key={i} className="text-base leading-relaxed text-[#3e516a] md:text-lg">
                {para}
              </p>
            ))}
          </Reveal>
        </div>}

        <div className="mt-2 grid grid-cols-1 gap-5 sm:grid-cols-3 md:mt-4">
          {a.values.map((v, i) => {
            return (
              <Reveal
                key={v.title}
                delay={i * 0.08}
                className="card-lift flex items-center gap-5 rounded-3xl border border-black/5 bg-white p-5 sm:gap-6 sm:p-6"
              >
                <img
                  src={VALUE_IMAGES[i]}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="h-24 w-24 shrink-0 rounded-2xl object-cover sm:h-28 sm:w-28"
                />
                <div className="min-w-0">
                  <h3 className="font-display text-xl font-bold tracking-tight text-[#0a1a2e]">
                    {v.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#3e516a]">{v.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-20">
          <div className="flex items-end justify-between gap-6">
            <div>
              <Kicker>{a.historyKicker}</Kicker>
              <h3 className="mt-4 font-display text-3xl font-bold tracking-tight text-[#0a1a2e] sm:text-4xl">
                {a.historyTitle}
              </h3>
            </div>
            <span className="hidden text-sm font-semibold text-[#002355]/55 md:block">{a.historyNote}</span>
          </div>

          <div className="relative mt-10 overflow-x-auto pb-4">
            <div className="absolute left-6 right-6 top-[17px] h-px bg-[#002355]/15" />
            <div className="relative grid min-w-[960px] grid-cols-6 gap-5">
              {a.history.map((item, i) => (
                <div key={item.year} className="relative pt-9">
                  <span className="absolute left-0 top-0 flex h-9 w-9 items-center justify-center rounded-full border-4 border-[#f5f7fa] bg-[#002355] text-[10px] font-bold text-[#f5f7fa]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="font-display text-xl font-extrabold tracking-tight text-[#002355]">{item.year}</p>
                  <h4 className="mt-2 text-sm font-bold leading-snug text-[#0a1a2e]">{item.title}</h4>
                  <p className="mt-2 max-w-[170px] text-xs leading-relaxed text-[#3e516a]">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[1.3fr_1fr]">
          <Reveal className="rounded-3xl border border-black/5 bg-white p-8 md:p-10">
            <h3 className="font-display text-lg font-bold tracking-tight text-[#0a1a2e]">
              {a.awardsTitle}
            </h3>
            <ul className="mt-7 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
              {a.awards.map((award, i) => (
                <li key={`${award.title}-${award.year}`}>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        data-testid={`about-award-${i}`}
                        className="group/award flex w-full items-start gap-4 rounded-2xl py-3 text-left transition-colors duration-300 hover:bg-[#002355]/[0.04]"
                      >
                        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#e8a93d]/15 text-[#c99311] transition-colors duration-300 group-hover/award:bg-[#e8a93d] group-hover/award:text-[#002355]">
                          <Award size={18} strokeWidth={1.8} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold leading-snug text-[#0a1a2e]">{award.title}</p>
                          <div className="mt-1 flex items-center gap-2">
                            {award.year && (
                              <span className="text-xs font-semibold uppercase tracking-wide text-[#3e516a]/60">
                                {award.year}
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#002355]/0 transition-colors duration-300 group-hover/award:text-[#002355]/50">
                              <ZoomIn size={12} strokeWidth={2} />
                              {a.awardsHint}
                            </span>
                          </div>
                        </div>
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-h-[88vh] w-[92vw] max-w-2xl overflow-y-auto border-none bg-[#0a1a2e] p-3 sm:p-4">
                      <img
                        src={AWARD_IMAGES[i]}
                        alt={award.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full rounded-xl"
                      />
                      <DialogTitle className="font-display text-base font-semibold text-[#f5f7fa]">
                        {award.title}
                      </DialogTitle>
                      {award.year && (
                        <DialogDescription className="text-[#f5f7fa]/60">{award.year}</DialogDescription>
                      )}
                    </DialogContent>
                  </Dialog>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.08} className="flex flex-col justify-between rounded-3xl bg-[#002355] p-8 md:p-10">
            <div>
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-[#e8a93d]">
                <FileCheck2 size={22} strokeWidth={1.7} />
              </div>
              <h3 className="mt-6 font-display text-lg font-bold tracking-tight text-[#f5f7fa]">
                {a.license.title}
              </h3>
              <p className="mt-3 text-sm font-semibold text-[#f5f7fa]/85">
                {a.license.number} {a.license.date}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-[#f5f7fa]/60">{a.license.issuer}</p>
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="mt-8 overflow-hidden rounded-2xl border border-white/15 bg-white/10 text-left transition-colors duration-300 hover:border-[#e8a93d]/70"
                >
                  <img
                    src={licenseImage}
                    alt={a.license.title}
                    loading="lazy"
                    decoding="async"
                    className="h-44 w-full object-cover object-top opacity-95 transition-transform duration-500 hover:scale-[1.02]"
                  />
                  <span className="block px-4 py-3 text-xs font-bold text-[#f5f7fa]">
                    {lang === "kz" ? "Құжатты үлкейтіп ашу" : "Открыть превью документа"}
                  </span>
                </button>
              </DialogTrigger>
              <DialogContent className="max-h-[88vh] w-[92vw] max-w-3xl overflow-y-auto border-none bg-[#0a1a2e] p-3 sm:p-4">
                <img src={licenseImage} alt={a.license.title} loading="lazy" decoding="async" className="w-full rounded-xl" />
                <DialogTitle className="font-display text-base font-semibold text-[#f5f7fa]">
                  {a.license.title}
                </DialogTitle>
                <DialogDescription className="text-[#f5f7fa]/60">
                  {a.license.number} {a.license.date}
                </DialogDescription>
              </DialogContent>
            </Dialog>
            <Link
              to="/reporting"
              data-testid="about-license-link"
              className="mt-5 inline-flex items-center text-sm font-bold text-[#e8a93d] transition-colors duration-300 hover:text-white"
            >
              {a.license.note}
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
